import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { AlertTriangle, CheckCircle2, Clock, FileSearch, Sparkles, XCircle } from "lucide-react";
import "../../Home.css";
import { apiFetch } from "../../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../../utils/actionFeedback";
import { scoringLabel, scoringLanguageFromText } from "../../utils/localizedScoringLabels";
import AiDecisionNotice from "../AiDecisionNotice";
import {
  FieldLabel,
  StatusPill,
  ToolBody,
  ToolHeader,
  ToolPage,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
  textareaClass,
} from "./ToolPageChrome";

export default function SingleCVScan({ user, onScanConsumed }) {
  const [cvFile, setCvFile] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [loadingScan, setLoadingScan] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [scanId, setScanId] = useState(null);

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState("");

  // holds duplicate scan info
  const [duplicateAlert, setDuplicateAlert] = useState(null);
  // const [forceRescan, setForceRescan] = useState(false);
  const scoringLanguage = scoringLanguageFromText(targetRole);

  useLongActionNotice(loadingScan, "Single CV scan", 45000);
  useLongActionNotice(loadingSummary, "Single CV AI summary", 45000);

  // Fetch jobs on mount
  useEffect(() => {
    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        const res = await apiFetch("/jobs");
        const data = await res.json();
        if (!res.ok) {
          setJobs([]);
          setJobsError(data?.detail || "Failed to load jobs.");
          return;
        }

        setJobs(Array.isArray(data) ? data : []);
        setJobsError("");
      } catch (err) {
        console.error("Failed to fetch jobs:", err);
        setJobs([]);
        setJobsError("Failed to load jobs.");
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, []);

  const handleJobSelect = (jobId) => {
    setSelectedJobId(jobId);
    setScanResult(null);
    setAiSummary(null);
    setDuplicateAlert(null);

    const selectedJob = jobs.find((job) => String(job.id) === String(jobId));
    setRoleName(selectedJob?.name || "");
    setTargetRole(selectedJob?.description || "");
  };

  const handleSingleCVScan = async (forceRescan = false) => {
    if (!cvFile || !selectedJobId || !roleName || !targetRole) {
      toast.error("Upload a CV and select a job.");
      return;
    }

    const formData = new FormData();
    formData.append("cv_file", cvFile);
    formData.append("job_name", roleName);
    formData.append("job_description", targetRole);
    formData.append("user_id", user.id);
    if (forceRescan) {
      console.log("TRUE")
      formData.append("force_rescan", "true"); // <-- flag for backend
    }

    try {
      setLoadingScan(true);
      setScanResult(null);
      setAiSummary(null);
      setLoadingSummary(false);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/analyze_cv`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "");

      // ⚡ Duplicate detection
      if (data.is_duplicate && !forceRescan) {
        setDuplicateAlert({
          message: "This CV has already been scanned for this job!",
          scan: data
        });
        setScanResult(data); // show previous results
        return; // prevent overwriting
      }

      setDuplicateAlert(null);
      setScanResult(data);
      onScanConsumed?.(1);
      // setForceRescan(false); // reset after scan
      setScanId(data.scan_id);

      if (data.ai_summary === null) setLoadingSummary(true);
      else setAiSummary(data.ai_summary);
    } catch (err) {
      toast.error(actionFailureMessage("Single CV scan", err.message));
    } finally {
      setLoadingScan(false);
    }
  };

  useEffect(() => {
    if (!scanId) return;

    const fetchSummary = async () => {
      try {
        const res = await apiFetch(`/scan/${scanId}/summary`);
        const data = await res.json();
        setAiSummary(data.ai_summary);
      } catch (err) {
        console.error("Failed to fetch AI summary:", err);
      } finally {
        setLoadingSummary(false);
      }
    };

    setTimeout(fetchSummary, 1000);
  }, [scanId]);

  const isFree = user.plan === "free";
  const lockedBox = (message) => (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
      <p className="font-semibold">Upgrade to unlock</p>
      <p className="mt-1">{message}</p>
    </div>
  );

  const buildSingleScanScoreExplanation = () => {
    if (!scanResult) return "";

    const score = Math.round(scanResult.match_score || 0);

    if (isFree) {
      return `This CV received ${score}% based on its overall relevance to the selected job description. Upgrade to view skill-level matching evidence and detailed gaps.`;
    }

    const matchingSkills = scanResult.candidate_summary?.key_strengths || [];
    const missingSkills = scanResult.candidate_summary?.missing_areas || [];
    const parts = [
      "the score is based on detected job-skill coverage, important requirement overlap, and overall relevance to the selected job description."
    ];

    if (matchingSkills.length) {
      parts.push(`Matched signals include ${matchingSkills.slice(0, 4).join(", ")}.`);
    }

    if (missingSkills.length) {
      parts.push(`Main gaps detected: ${missingSkills.slice(0, 4).join(", ")}.`);
    } else {
      parts.push("No major missing skills were detected.");
    }

    return `This CV received ${score}% because ${parts.join(" ")}`;
  };

  const getScoringBreakdown = () =>
    scanResult?.scoring_breakdown || scanResult?.candidate_summary?.scoring_breakdown || null;

  const renderScoreBreakdown = () => {
    if (!scanResult) return null;

    const breakdown = getScoringBreakdown();
    const finalScore = Math.round(scanResult.match_score || 0);

    if (!breakdown) {
      return (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">{scoringLabel("Score Breakdown", scoringLanguage)}</h3>
              <p className="mt-1 text-sm text-slate-500">
                {scoringLabel("This score uses semantic CV-to-job similarity and detected skill coverage.", scoringLanguage)}
              </p>
            </div>
            <span className="w-fit rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-bold text-slate-800">
              {scoringLabel("Final", scoringLanguage)} {finalScore}%
            </span>
          </div>
        </div>
      );
    }

    const rows = [
      {
        label: scoringLabel("Baseline match", scoringLanguage),
        value: Math.round(breakdown.base_score || 0),
        description: scoringLabel("Broad CV-to-job relevance plus detected role-skill overlap before recruiter criteria.", scoringLanguage),
      },
    ];

    if (breakdown.must_score !== null && breakdown.must_score !== undefined) {
      rows.push({
        label: scoringLabel("Must-have criteria", scoringLanguage),
        value: Math.round(breakdown.must_score || 0),
        description: scoringLabel("Recruiter-selected critical skills. Weak evidence here can cap the final score.", scoringLanguage),
      });
    }

    if (breakdown.preferred_score !== null && breakdown.preferred_score !== undefined) {
      rows.push({
        label: scoringLabel("Preferred criteria", scoringLanguage),
        value: Math.round(breakdown.preferred_score || 0),
        description: scoringLabel("Bonus skills that improve confidence, but should not reject a candidate by themselves.", scoringLanguage),
      });
    }

    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="font-semibold text-emerald-950">{scoringLabel("Score Breakdown", scoringLanguage)}</h3>
            <p className="mt-1 text-sm leading-6 text-emerald-800">
              {scoringLabel("The final score combines CV-to-job relevance, skill evidence, and recruiter-defined criteria.", scoringLanguage)}
            </p>
          </div>
          <span className="w-fit rounded-full bg-white px-3 py-1 text-sm font-bold text-emerald-900">
            {scoringLabel("Final", scoringLanguage)} {finalScore}%
          </span>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {rows.map((row) => (
            <div key={row.label} className="rounded-lg bg-white/85 p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">{row.label}</p>
                <span className="text-sm font-bold text-emerald-800">{row.value}%</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-emerald-700"
                  style={{ width: `${Math.max(0, Math.min(100, row.value))}%` }}
                />
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-500">{row.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-md border border-emerald-100 bg-white/70 p-3 text-xs leading-5 text-emerald-900">
          <span className="font-semibold">{scoringLabel("How to read this:", scoringLanguage)}</span>{" "}
          {scoringLabel("use the final score for quick ranking, then use the breakdown to see whether the score came from broad relevance, required skills, or bonus criteria.", scoringLanguage)}
        </div>

        {((breakdown.must_have || []).length > 0 || (breakdown.preferred || []).length > 0) && (
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {(breakdown.must_have || []).map((item) => (
              <div key={`single-must-${item.skill}`} className="rounded-md bg-white/85 p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-slate-900">{item.skill}</span>
                  <span className={`rounded-full px-2 py-1 text-xs font-bold ${
                    item.passed ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                  }`}>
                    {Math.round(item.score || 0)}% / {Math.round(item.required || 0)}%
                  </span>
                </div>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {scoringLabel("Must-have", scoringLanguage)} {scoringLabel(item.passed ? "passed" : "below target", scoringLanguage)}
                </p>
              </div>
            ))}
            {(breakdown.preferred || []).map((item) => (
              <div key={`single-preferred-${item.skill}`} className="rounded-md bg-white/85 p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-slate-900">{item.skill}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700">
                    {Math.round(item.score || 0)}%
                  </span>
                </div>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {scoringLabel("Preferred", scoringLanguage)} {scoringLabel(item.found ? "found" : "not found", scoringLanguage)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <ToolPage>
        <ToolHeader
          icon={FileSearch}
          title="Single CV Scan"
          description="Upload one candidate CV, attach it to a target role, and review match evidence before making a shortlist decision."
          meta={<StatusPill tone={isFree ? "amber" : "green"}>{isFree ? "AI summary locked" : "AI summary enabled"}</StatusPill>}
        />
        <ToolBody className="space-y-5">

      <div>
        <FieldLabel>Candidate CV</FieldLabel>
      <input
        type="file"
        accept=".pdf"
        onChange={(e) => setCvFile(e.target.files[0])}
        className={`${inputClass} file:mr-4 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-200`}
      />
      </div>

      <div>
        <FieldLabel>Job</FieldLabel>
        <select
          value={selectedJobId}
          onChange={(e) => handleJobSelect(e.target.value)}
          className={inputClass}
          disabled={loadingJobs}
        >
          <option value="">{loadingJobs ? "Loading jobs..." : "Select a job"}</option>
          {jobs.map((job) => (
            <option key={job.id} value={job.id}>
              {job.name}
            </option>
          ))}
        </select>
        {jobs.length === 0 && !loadingJobs && (
          <p className="mt-2 text-sm text-amber-700">
            {jobsError || "Create a job first from the Jobs page, then return here to scan a CV."}
          </p>
        )}
      </div>

      {/* Role name & description */}
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <FieldLabel>Target Role</FieldLabel>
      <input
        type="text"
        placeholder="Target role name"
        value={roleName}
        className={inputClass}
        readOnly
      />
        </div>
        <div>
          <FieldLabel>Job Description</FieldLabel>
      <textarea
        placeholder="Job description"
        value={targetRole}
        className={`${textareaClass} min-h-[44px]`}
        readOnly
      />
        </div>
      </div>

      <button
        onClick={() => handleSingleCVScan(false)}
        disabled={loadingScan}
        className={primaryButtonClass}
      >
        <Sparkles size={16} />
        {loadingScan ? "Analyzing..." : "Analyze CV"}
      </button>

      {loadingScan && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
            <span className="font-semibold text-slate-900">Scanning CV</span>
            <span className="text-slate-500">Extracting text, matching skills, and scoring against the selected job</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-200">
            <div className="h-full w-2/3 animate-pulse rounded-full bg-emerald-800" />
          </div>
        </div>
      )}

      {duplicateAlert && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="font-semibold text-amber-950">{duplicateAlert.message}</p>
          <p className="mt-1 text-sm text-amber-800">
            Previous scan result will be shown below. You can see the star (shortlisted) and notes.
          </p>
          <button
            onClick={() => handleSingleCVScan(true)} // pass `true` to forceRescan
            className={`${secondaryButtonClass} mt-3`}
          >
            Rescan Anyway
          </button>
        </div>
      )}

      {scanResult && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-6">
          <AiDecisionNotice />

          {/* Match Score Section */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-700">Match Score</h3>
              <p className="text-4xl font-bold text-slate-950">
                {scanResult.match_score?.toFixed(2)}%
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
              scanResult.match_score >= 50 ? "bg-emerald-100 text-emerald-700"
              : scanResult.match_score >= 35 ? "bg-amber-100 text-amber-700"
              : "bg-rose-100 text-rose-700"
            }`}>
              {scanResult.match_score >= 50 ? "Strong Fit"
                : scanResult.match_score >= 35 ? "Moderate Fit"
                : "Low Fit"}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="font-semibold text-slate-900">{scoringLabel("Why this score?", scoringLanguage)}</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {buildSingleScanScoreExplanation()}
            </p>
          </div>

          {renderScoreBreakdown()}

          {/* AI Summary Card */}
          <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg" >
            <h3 className="font-semibold text-slate-900 mb-2">AI Candidate Summary</h3>

            {isFree && (
              lockedBox("AI summary is available on Basic and Pro plans.")
            )}
            {!isFree && (
              <div className="mt-2 text-slate-700">
                {loadingSummary ? (
                  <div className="relative mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div className="ai-loading-bar"></div>
                  </div>
                ) : aiSummary ? (
                  <p className="mt-2 whitespace-pre-line text-slate-700">{aiSummary}</p>
                ) : (
                  <p className="text-slate-500">No summary generated.</p>
                )}
              </div>
            )}
          </div>

          {/* (rest of your UI unchanged) */}

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h4 className="mb-2 flex items-center gap-2 font-semibold text-slate-800">
                <CheckCircle2 size={16} className="text-emerald-600" />
                {scoringLabel("Detected Skill Evidence", scoringLanguage)}
              </h4>
              <div className="flex flex-wrap gap-2">
                {scanResult.candidate_summary?.key_strengths?.length > 0 ? (
                  scanResult.candidate_summary.key_strengths.map((skill, idx) => (
                    <span
                      key={idx}
                      className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700"
                    >
                      {skill}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No strong matches detected</p>
                )}
              </div>
            </div>

            <div>
              <h4 className="mb-2 flex items-center gap-2 font-semibold text-slate-800">
                <XCircle size={16} className="text-rose-600" />
                {scoringLabel("Unconfirmed Skill Evidence", scoringLanguage)}
              </h4>
              {scanResult.locked_features?.missing_skills ? (
                lockedBox("Skill gap analysis is available on Basic and Pro plans.")
              ) : (
                <div className="flex flex-wrap gap-2">
                  {scanResult.candidate_summary?.missing_areas?.length > 0 ? (
                    scanResult.candidate_summary.missing_areas.map((skill, idx) => (
                      <span
                        key={idx}
                        className="rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-600"
                      >
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No major gaps detected</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-6 border-t pt-4 md:grid-cols-2">
            <div>
              <h4 className="mb-1 font-semibold text-slate-800">
                Education & certifications
              </h4>
              <p className="text-sm text-slate-600">
                {scanResult.candidate_summary?.top_qualification || scanResult.top_qualification || "Not detected"}
              </p>
            </div>

            <div>
              <h4 className="mb-1 font-semibold text-slate-800">
                Titles held by applicant
              </h4>
              {scanResult.locked_features?.job_titles ? (
                lockedBox("Applicant title extraction is available on Basic and Pro plans.")
              ) : (
                <p className="text-sm text-slate-600">
                  {scanResult.candidate_summary?.job_titles?.join(", ") || "Not detected"}
                </p>
              )}
            </div>

            <div>
              <h4 className="mb-1 flex items-center gap-2 font-semibold text-slate-800">
                <Clock size={16} className="text-slate-500" />
                Experience Estimate
              </h4>
              {scanResult.locked_features?.experience_years ? (
                lockedBox("Experience estimation is available on Basic and Pro plans.")
              ) : (
                <p className="text-sm text-slate-600">
                  {scanResult.candidate_summary?.experience_estimate || "Unable to confidently estimate"}
                </p>
              )}
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="mb-2 flex items-center gap-2 font-semibold text-slate-800">
              <AlertTriangle size={16} className="text-amber-600" />
              Hiring Recommendation
            </h4>
            <p className="text-sm font-medium text-slate-700">
              {scanResult.match_score >= 50
                ? "Strong candidate. Recommend proceeding to interview."
                : scanResult.match_score >= 35
                ? "Potential candidate. Consider interview depending on team flexibility."
                : "Limited alignment. Interview not recommended unless role requirements are flexible."}
            </p>
          </div>
        </div>
      )}
        </ToolBody>
      </ToolPage>
    </motion.div>
  );
}
